import React, { useState, useEffect, useCallback, useRef } from "react";
import "./WordScramble.css";

function WordScramble({ onBack, isPaused }) {
    const [words, setWords] = useState([]);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [scrambledLetters, setScrambledLetters] = useState([]);
    const [score, setScore] = useState(0);
    const [bestScore, setBestScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(30);
    const [isGameOver, setIsGameOver] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [status, setStatus] = useState(""); // "correct", "wrong", ""
    const [gameStarted, setGameStarted] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const TOTAL_WORDS = 10;

    const timerRef = useRef(null);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    // Load best score
    useEffect(() => {
        const savedBest = localStorage.getItem("wordScramble_bestScore");
        if (savedBest) setBestScore(parseInt(savedBest));
    }, []);

    const fetchDynamicWords = async () => {
        setLoading(true);
        // Expanded static list (30 words)
        const fallback = [ 
            "LAMP", "FISH", "BIRD", "STAR", "MOON", "SHIP", "TREE", "CAKE", "FROG", "LION", 
            "DUCK", "BIKE", "KITE", "ROSE", "FIRE", "SNOW", "RAIN", "WIND", "GOLD", "BOOK",
            "MILK", "SOUP", "COIN", "DESK", "DOOR", "BELL", "ROCK", "SAND", "LEAF", "BUSH"
        ];
        // Shuffle the list so every game is different
        const shuffled = [...fallback].sort(() => Math.random() - 0.5);
        const formattedFallback = shuffled.map(word => ({ word }));
        setWords(formattedFallback);
        setLoading(false);
        return formattedFallback;
    };

    const scramble = useCallback((word) => {
        let arr = word.split("").map((char, index) => ({ char, id: index }));
        let attempts = 0;
        while (attempts < 10) {
            for (let i = arr.length - 1; i > 0; i--) {
                let j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            if (arr.map(item => item.char).join("") !== word) break;
            attempts++;
        }
        return arr;
    }, []);

    const startNewWord = useCallback((wordList, index) => {
        if (!wordList || !wordList[index]) return;        
        const nextWord = wordList[index];
        setScrambledLetters(scramble(nextWord.word));
        setShowHint(false);
        setStatus("");
        setMessage("");
        setTimeLeft(30);
    }, [scramble]);

    const startGame = async () => {
        const freshWords = await fetchDynamicWords();
        setGameStarted(true);
        setScore(0);
        setWordCount(1);
        setCurrentWordIndex(0);
        setIsGameOver(false);
        startNewWord(freshWords, 0);
    };

    useEffect(() => {
        if (gameStarted && !isGameOver && timeLeft > 0 && !loading && !isPaused) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && gameStarted && !isGameOver && !isPaused) {
            const currentWord = words[currentWordIndex]?.word || "";
            setMessage(`Time's up! It was '${currentWord}'`);
            setStatus("wrong");
            setTimeout(() => handleNextWord(), 2000);
        }

        return () => clearInterval(timerRef.current);
    }, [gameStarted, isGameOver, timeLeft, currentWordIndex, loading, words, isPaused]);

    const handleNextWord = useCallback(() => {
        if (wordCount >= TOTAL_WORDS) {
            setIsGameOver(true);
            setGameStarted(false);
            if (score > bestScore) {
                setBestScore(score);
                localStorage.setItem("wordScramble_bestScore", score.toString());
            }
        } else {
            const nextIdx = (currentWordIndex + 1) % words.length;
            setWordCount(prev => prev + 1);
            setCurrentWordIndex(nextIdx);
            startNewWord(words, nextIdx);
        }
    }, [wordCount, TOTAL_WORDS, score, bestScore, currentWordIndex, words, startNewWord]);

    const checkResult = (letters) => {
        const currentWord = words[currentWordIndex]?.word;
        const formedWord = letters.map(l => l.char).join("");
        
        if (formedWord === currentWord) {
            setStatus("correct");
            setScore(prev => prev + 10 + (showHint ? 0 : 5));
            setTimeout(() => handleNextWord(), 1000);
        }
    };

    const handleDragStart = (e, index) => { dragItem.current = index; };
    const handleDragEnter = (e, index) => { dragOverItem.current = index; };
    const handleDragEnd = () => {
        if (dragItem.current === null || dragOverItem.current === null) return;
        const copyListItems = [...scrambledLetters];
        const dragItemContent = copyListItems[dragItem.current];
        copyListItems.splice(dragItem.current, 1);
        copyListItems.splice(dragOverItem.current, 0, dragItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setScrambledLetters(copyListItems);
        checkResult(copyListItems);
    };

    const handleTouchStart = (index) => { 
        dragItem.current = index; 
        // Add visual feedback class to the element
        const el = document.querySelector(`.ws_letter-box[data-index="${index}"]`);
        if (el) el.classList.add("ws_dragging");
    };

    const handleTouchMove = (e) => {
        if (dragItem.current === null) return;
        if (e.cancelable) e.preventDefault(); // Prevent scrolling

        const touch = e.touches[0];
        // Use elementsFromPoint to find what's under the finger
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const target = elements.find(el => el.classList.contains("ws_letter-box"));
        
        if (target) {
            const idx = target.getAttribute("data-index");
            if (idx !== null) {
                const newIdx = parseInt(idx);
                if (newIdx !== dragOverItem.current) {
                    dragOverItem.current = newIdx;
                }
            }
        }
    };

    const handleTouchEnd = () => {
        // Remove visual feedback from all boxes
        document.querySelectorAll(".ws_letter-box").forEach(el => el.classList.remove("ws_dragging"));
        
        if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
            handleDragEnd();
        }
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const useHint = () => {
        if (!showHint && !loading) {
            setScore(prev => Math.max(0, prev - 5));
            setShowHint(true);
        }
    };

    const resetCurrentWord = () => {
        if (words[currentWordIndex]) {
            setScrambledLetters(scramble(words[currentWordIndex].word));
            setStatus("");
            setMessage("");
        }
    };

    return (
        <div className="ws_main-container">
            <div className="ws_game-wrapper">
                {!gameStarted && !isGameOver ? (
                    <div className="ws_card ws_intro-card">
                        <div className="ws_title-section">
                            <div className="ws_logo">ABC</div>
                            <h1>Word <span>Scramble</span></h1>
                            <p>Unscramble letters to find the word!</p>
                        </div>                        
                        <div className="ws_btn-group">
                            <button className="ws_btn ws_btn-primary" onClick={startGame} disabled={loading}>
                                {loading ? "Loading..." : "Play Now"}
                            </button>
                            <button className="ws_btn ws_btn-outline" onClick={onBack}>Back</button>
                        </div>
                    </div>
                ) : isGameOver ? (
                    <div className="ws_card ws_result-card">
                        <h2>Finished!</h2>
                        <div className="ws_score-display">
                            <div className="ws_score-circle">
                                <span className="ws_score-val">{score}</span>
                                <span className="ws_score-label">Points</span>
                            </div>
                        </div>
                        <div className="ws_stats-grid">
                            <div className="ws_stat-box">
                                <label>BEST</label>
                                <span>{bestScore}</span>
                            </div>
                            <div className="ws_stat-box">
                                <label>WORDS</label>
                                <span>{TOTAL_WORDS}</span>
                            </div>
                        </div>
                        <div className="ws_btn-group">
                            <button className="ws_btn ws_btn-primary" onClick={startGame}>Replay</button>
                            <button className="ws_btn ws_btn-outline" onClick={onBack}>Exit</button>
                        </div>
                    </div>
                ) : (
                    <div className="ws_card ws_play-card">
                        <div className="ws_play-header">
                            <div className="ws_hud-item">
                                <span className="ws_hud-label">SCORE</span>
                                <span className="ws_hud-val">{score}</span>
                            </div>
                            <div className="ws_progress">
                                <span>{wordCount} / {TOTAL_WORDS}</span>
                                <div className="ws_progress-bar">
                                    <div className="ws_progress-fill" style={{ width: `${(wordCount / TOTAL_WORDS) * 100}%` }}></div>
                                </div>
                            </div>
                            <div className={`ws_hud-item ws_timer ${timeLeft < 10 ? "ws_urgent" : ""}`}>
                                <span className="ws_hud-label">TIME</span>
                                <span className="ws_hud-val">{timeLeft}s</span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="ws_loader">
                                <div className="ws_spinner"></div>
                                <p>Loading words...</p>
                            </div>
                        ) : (
                            <div className="ws_game-area">
                                <h2 className="ws_game-instruction">Rearrange letters</h2>
                                <div className="ws_letters-row">
                                    {scrambledLetters.map((item, index) => (
                                        <div
                                            key={item.id}
                                            data-index={index}
                                            className={`ws_letter-box ${status}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnter={(e) => handleDragEnter(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => e.preventDefault()}
                                            onTouchStart={() => handleTouchStart(index)}
                                            onTouchMove={handleTouchMove}
                                            onTouchEnd={handleTouchEnd}
                                        >
                                            <span className="ws_letter">{item.char}</span>
                                            <div className="ws_letter-shadow"></div>
                                        </div>
                                    ))}
                                </div>
                                <div className="ws_feedback-area">
                                    {message ? (
                                        <div className={`ws_status-banner ${status}`}>{message}</div>
                                    ) : showHint ? (
                                        <div className="ws_hint-box">
                                            Hint: Starts with '{words[currentWordIndex]?.word[0] || ""}'
                                        </div>
                                    ) : (
                                        <div className="ws_drag-tip">Drag to reorder</div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="ws_play-footer">
                            <button className="ws_btn-icon" onClick={resetCurrentWord}>Reset</button>
                            <button className={`ws_btn-icon ${showHint ? "active" : ""}`} onClick={useHint} disabled={showHint || loading}>Hint (-5)</button>
                            <button className="ws_btn-icon" onClick={onBack}>Exit</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WordScramble;
